import React, { useEffect, useMemo, useState } from 'react'
import { AudioTransport } from '@mytours/components'
import { AudioPreview, AudioSectionContainer, Screen } from 'mytours-read-common'

import strings from '../../../../strings'
import { DEFAULT_SKIP_SECONDS, useStopAudio } from '../../useStopAudio'
import TranscriptSheet from '../TranscriptSheet'

interface Props {
	audioPreview?: AudioPreview
	container?: AudioSectionContainer
	collectionId?: number
	screen?: Screen
	// Names the controls: "Play audio track <name>". The stop row passes its own name, not the audio file's.
	name: string
	className?: string
	// The seam the rate menu will be wired through.
	onRateChange?: () => void
}

/**
 * The stop row's expanded transport: `AudioTransport` draws it, `useStopAudio` drives it, and the transcript
 * and rate controls appear only when there is something behind them. Unmounting does not stop the sound.
 */
const AudioPlayerTransport: React.FC<Props> = ({
	audioPreview,
	container,
	collectionId,
	screen,
	name,
	className,
	onRateChange,
}) => {
	const [transcriptOpen, setTranscriptOpen] = useState(false)
	const hasTranscript = Boolean(audioPreview?.transcription)

	const track = useMemo(
		() => ({ audioPreview, container, collectionId, screen }),
		[audioPreview, container, collectionId, screen],
	)
	const { playable, isPlaying, currentTime, duration, playPause, seek, skip, preload } = useStopAudio(track)

	// The duration and scrub bar stay blank until a Howl reads the file's metadata, so opening a row builds it.
	useEffect(() => {
		if (!playable) return
		void preload()
	}, [playable, preload])

	const labels = useMemo(
		() => ({
			seek: strings.accessibility_audio_seek,
			play: strings.formatString(strings.accessibility_play_audio_s, { title: name }) as string,
			pause: strings.formatString(strings.accessibility_pause_audio_s, { title: name }) as string,
			skipBack: strings.formatString(strings.accessibility_seek_backward, {
				seconds: DEFAULT_SKIP_SECONDS,
			}) as string,
			skipForward: strings.formatString(strings.accessibility_seek_forward, {
				seconds: DEFAULT_SKIP_SECONDS,
			}) as string,
			rate: strings.accessibility_playback_speed,
			transcript: strings.transcript,
		}),
		[name],
	)

	// Open-only despite the name: the sheet closes itself.
	const openTranscript = () => setTranscriptOpen(true)

	if (!playable) {
		return null
	}

	return (
		<>
			<AudioTransport
				className={className}
				currentTime={currentTime}
				duration={duration}
				isPlaying={isPlaying}
				skipSeconds={DEFAULT_SKIP_SECONDS}
				labels={labels}
				hasTranscript={hasTranscript}
				showRate={Boolean(onRateChange)}
				onPlay={playPause}
				onPause={playPause}
				onSeek={seek}
				onSkip={skip}
				onRateChange={onRateChange}
				onTranscriptToggle={openTranscript}
			/>
			{/* One `useStopAudio` feeds both, so the sheet and the transport cannot disagree about the position. */}
			{hasTranscript && transcriptOpen && (
				<TranscriptSheet
					open
					onClose={() => setTranscriptOpen(false)}
					title={name}
					transcription={audioPreview?.transcription}
					transcriptionFormat={audioPreview?.transcription_format}
					mediaItemId={audioPreview?.id}
					currentTime={currentTime}
					isPlaying={isPlaying}
				/>
			)}
		</>
	)
}

export default AudioPlayerTransport
